import { db } from '../../utils/firebase';
import { formatTime, calculateDuration } from '../../utils/dateUtils';
import React from 'react';
import { collection, getDocs } from 'firebase/firestore';
import { useNavigate } from 'react-router-dom';

// Helper function to safely format time from timestamps
const safeFormatTime = (timestamp) => {
  try {
    if (!timestamp) return 'N/A';
    
    // Handle Firestore Timestamp objects
    if (timestamp && typeof timestamp.toDate === 'function') {
      return formatTime(timestamp.toDate());
    }
    
    // Handle Date objects
    if (timestamp instanceof Date) {
      return formatTime(timestamp);
    }
    
    // Handle string timestamps
    if (typeof timestamp === 'string') {
      return formatTime(new Date(timestamp));
    }
    
    return 'Invalid time';
  } catch (error) {
    console.error('Error formatting time:', error);
    return 'Invalid time';
  }
};

// Helper function to safely calculate duration
const safeCalculateDuration = (start, end) => {
  try {
    if (!start || !end) return 'N/A';
    
    // Convert Firestore Timestamps to Date objects if needed
    const startDate = typeof start.toDate === 'function' ? start.toDate() : new Date(start);
    const endDate = typeof end.toDate === 'function' ? end.toDate() : new Date(end);
    
    return calculateDuration(startDate, endDate);
  } catch (error) {
    console.error('Error calculating duration:', error);
    return 'N/A';
  }
};

const AgendaView = ({ 
    onViewTrailDetails, 
    onViewProfile, 
    onAcceptMatch, 
    onCancelMatch,
    onFeedbackRequest,
    matches, 
    user 
}) => {
    const [selectedDate, setSelectedDate] = React.useState(new Date());
    const [profiles, setProfiles] = React.useState({});
    const [trails, setTrails] = React.useState({});
    const navigate = useNavigate();

    // Helper function to safely handle trail details navigation
    const handleViewTrailDetails = (trailId, session, source) => {
        console.log("Handling view trail details for:", trailId);
        
        try {
            // Get the trail object
            const trail = trails[trailId];
            
            if (!trail) {
                console.error("Trail not found:", trailId);
                return;
            }
            
            console.log("Trail object:", trail);
            console.log("Session object:", session);
            
            // Log the onViewTrailDetails function
            console.log("onViewTrailDetails function:", onViewTrailDetails);
            
            // Call the parent component's function with the trail object AND timing info
            if (typeof onViewTrailDetails === 'function') {
                // Try different parameter combinations
                try {
                    console.log("Calling onViewTrailDetails with trail and timing");
                    onViewTrailDetails(trail, {
                        start_time: session.start_time,
                        end_time: session.end_time
                    }, source);
                } catch (callError) {
                    console.error("Error calling with trail and timing:", callError);
                    
                    try {
                        console.log("Trying with just the trail object");
                        onViewTrailDetails(trail);
                    } catch (trailError) {
                        console.error("Error calling with just trail:", trailError);
                        
                        try {
                            console.log("Trying with just the trail ID");
                            onViewTrailDetails(trailId);
                        } catch (idError) {
                            console.error("Error calling with just ID:", idError);
                        }
                    }
                }
            } else {
                console.error("onViewTrailDetails is not a function:", onViewTrailDetails);
            }
        } catch (error) {
            console.error("Error in handleViewTrailDetails:", error);
            
            // Fallback to direct navigation
            try {
                console.log("Attempting direct navigation");
                navigate(`/trail/${trailId}`);
            } catch (navError) {
                console.error("Direct navigation failed:", navError);
            }
        }
    };

    // Filter matches for current user
    const userMatches = matches?.filter(match => {
        // First filter out cancelled matches
        if (match.status === 'cancelled' || match.status === 'canceled') return false;
        
        if (match.status === 'searching') {
            // Only show searching matches to the creator
            return match.profile_ids.id1 === user.uid;
        } else if (match.status === 'pending') {
            // For pending matches, show to both users
            return match.profile_ids.id1 === user.uid || match.profile_ids.id2 === user.uid;
        } else {
            // For confirmed/other statuses, show to both users
            return match.profile_ids.id1 === user.uid || match.profile_ids.id2 === user.uid;
        }
    }) || [];
    const uniqueSessionsMap = new Map();

if (userMatches && userMatches.length > 0) {
  userMatches.forEach((session) => {
    const key =
      session.status === 'confirmed'
        ? `${session.trail_id}_${session.start_time?.seconds}_${(session.participants || []).sort().join('_')}`
        : session.id;

    if (!uniqueSessionsMap.has(key)) {
      uniqueSessionsMap.set(key, session);
    }
  });
}

const dedupedMatches = Array.from(uniqueSessionsMap.values());

    // Fetch profiles and trails data
    React.useEffect(() => {
        const fetchData = async () => {
            try {
                // Fetch profiles
                const profilesSnapshot = await getDocs(collection(db, 'users'));
                const profilesData = {};
                profilesSnapshot.docs.forEach(doc => {
                    profilesData[doc.id] = { id: doc.id, ...doc.data() };
                });
                setProfiles(profilesData);

                // Fetch trails
                const trailsSnapshot = await getDocs(collection(db, 'trails'));
                const trailsData = {};
                trailsSnapshot.docs.forEach(doc => {
                    trailsData[doc.id] = { id: doc.id, ...doc.data() };
                });
                setTrails(trailsData);
            } catch (error) {
                console.error("Error fetching data:", error);
            }
        };

        fetchData();
    }, []);

    // Helper function to format date to YYYY-MM-DD
    const formatDate = (date) => {
        if (!date) {
            return '';
        }
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        return `${year}-${month}-${day}`;
    };
    

    // Get calendar data
    const getDaysInMonth = (year, month) => new Date(year, month + 1, 0).getDate();
    const getFirstDayOfMonth = (year, month) => new Date(year, month, 1).getDay();
    
    const currentYear = selectedDate.getFullYear();
    const currentMonth = selectedDate.getMonth();
    const daysInMonth = getDaysInMonth(currentYear, currentMonth);
    const firstDayOfMonth = getFirstDayOfMonth(currentYear, currentMonth);
    
    const monthNames = ["January", "February", "March", "April", "May", "June",
        "July", "August", "September", "October", "November", "December"];
    
    // Generate calendar days
    const days = [];

    for (let i = 0; i < firstDayOfMonth; i++) {
        days.push(<div key={`empty-${i}`} className="p-2"></div>);
    }
    
    // Helper function to check if there are sessions on a specific date
    const hasSessionsOnDate = (date, sessions) => {
        if (!sessions || !sessions.length) return false;
        
        return sessions.some(session => {
            if (!session.start_time) return false;
            
            // Handle Firestore Timestamp objects
            let sessionDate;
            if (session.start_time.seconds) {
                // It's a Firestore Timestamp
                sessionDate = new Date(session.start_time.seconds * 1000);
            } else {
                // It's a regular Date or string
                sessionDate = new Date(session.start_time);
            }
            
            sessionDate.setHours(0, 0, 0, 0);
            const compareDate = new Date(date);
            compareDate.setHours(0, 0, 0, 0);
            return sessionDate.getTime() === compareDate.getTime();
        });
    };

    // Add the actual days
    for (let day = 1; day <= daysInMonth; day++) {
        const currentDate = new Date(currentYear, currentMonth, day);
        const hasEvents = hasSessionsOnDate(currentDate, userMatches);
        
        days.push(
            <div 
                key={`day-${day}`} 
                onClick={() => setSelectedDate(new Date(currentYear, currentMonth, day))}
                className={`p-2 text-center cursor-pointer hover:bg-gray-100 rounded-lg ${
                    formatDate(new Date(currentYear, currentMonth, day)) === formatDate(selectedDate)
                        ? 'bg-purple-100'
                        : hasEvents 
                            ? 'bg-blue-50' 
                            : ''
                }`}
            >
                <span className={`${hasEvents ? 'text-blue-600 font-semibold' : ''}`}>
                    {day}
                </span>
            </div>
        );
    }

    // Update the sessions list to show all matches for the selected date
    const sessionsForSelectedDate = dedupedMatches.filter(session => {
        if (!session.start_time) return false;
        
        // Handle Firestore Timestamp objects
        let sessionDate;
        if (session.start_time.seconds) {
            // It's a Firestore Timestamp
            sessionDate = new Date(session.start_time.seconds * 1000);
        } else {
            // It's a regular Date or string
            sessionDate = new Date(session.start_time);
        }
        
        const selectedDateStart = new Date(selectedDate);
        // Reset hours to compare only dates
        selectedDateStart.setHours(0, 0, 0, 0);
        sessionDate.setHours(0, 0, 0, 0);
        
        
        return sessionDate.getTime() === selectedDateStart.getTime();
    }).map(session => {
        const trail = trails[session.trail_id];
        const otherPlayerId = session.profile_ids.id1 === user.uid 
            ? session.profile_ids.id2 
            : session.profile_ids.id1;
        const otherProfile = profiles[otherPlayerId];

        // Add feedback check
        const userFeedback = session.feedback?.[user.uid];
        const canProvideFeedback = session.status === 'completed' && 
            (!userFeedback || (userFeedback.skipped && !userFeedback.attended));

        return {
            ...session,
            trail,
            otherProfile,
            canProvideFeedback
        };
    }) || [];

    // Sort the sessions
    const sortedSessions = sessionsForSelectedDate.sort((a, b) => {
        // Sort by time, then by status (confirmed first, pending second, searching last)
        const timeA = new Date(a.start_time).getTime();
        const timeB = new Date(b.start_time).getTime();
        if (timeA !== timeB) return timeA - timeB;
        
        const statusPriority = {
            'confirmed': 0,
            'pending': 1,
            'searching': 2
        };
        
        return (statusPriority[a.status.toLowerCase()] || 0) - (statusPriority[b.status.toLowerCase()] || 0);
    });

    

    return (
    <div className="min-h-screen bg-white p-6 flex flex-col items-center">
        <header className="mb-6 text-center">
            <h1 className="text-3xl font-bold">Your Schedule</h1>
            <p className="text-gray-600">Track your past and future</p>
        </header>

            {/* Calendar Header */}
        <div className="mb-4 flex justify-between items-center w-full max-w-lg">
            <h2 className="text-xl font-semibold text-center">
                {monthNames[currentMonth]} {currentYear}
            </h2>
            <div className="space-x-2">
                <button 
                    onClick={() => setSelectedDate(new Date(currentYear, currentMonth - 1))}
                    className="px-3 py-1 rounded-full bg-gray-100"
                >
                    ←
                </button>
                <button 
                    onClick={() => setSelectedDate(new Date(currentYear, currentMonth + 1))}
                    className="px-3 py-1 rounded-full bg-gray-100"
                >
                    →
                </button>
            </div>
        </div>

            {/* Calendar Grid */}
        <div className="mb-6 w-full max-w-lg">
            <div className="grid grid-cols-7 gap-1 mb-2 text-center text-gray-500 text-sm">
                {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(day => (
                    <div key={day} className="text-center">
                        {day}
                    </div>
                ))}
            </div>
            <div className="grid grid-cols-7 gap-1">
                {days}
            </div>
        </div>

            {/* Sessions List */}
        <div className="space-y-4 w-full max-w-lg">
            {sortedSessions.map((session, index) => {
                const statusClass = session.status === 'pending' 
                    ? 'bg-yellow-100 text-yellow-800' 
                    : session.status === 'searching' 
                        ? 'bg-blue-100 text-blue-800' 
                        : session.status === 'completed'
                            ? 'bg-green-100 text-green-800'
                            : 'bg-green-100 text-green-800';
                    
                    return (
                    <div key={index} className="bg-white rounded-lg shadow-md p-4 mb-4 border border-gray-200 mx-auto">
                        <div className="flex justify-between items-start mb-3">
                            <div className="text-center w-full">
                                <h3 className="text-lg font-semibold text-gray-800">{session.trail?.name || 'Unknown Trail'}</h3>
                                <p className="text-sm text-gray-600">with {session.otherProfile?.name || 'Unknown Player'}</p>
                            </div>
                            <span className={`px-2 py-1 rounded-full text-xs font-medium ${statusClass}`}>
                                {session.status.charAt(0).toUpperCase() + session.status.slice(1)}
                            </span>
                        </div>
                            <div className="mt-4 space-y-2 text-sm text-gray-600">
                                <div className="flex items-center">
                                    <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" 
                                            d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                                    </svg>
                                    <span>{selectedDate.toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' })}</span>
                                </div>
                                <div className="flex items-center">
                                    <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" 
                                            d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                                    </svg>
                                    <span>
                                        {safeFormatTime(session.start_time)} (Est. {session.trail?.hours ?? safeCalculateDuration(session.start_time, session.end_time)} hrs)
                                    </span>
                                </div>
                                <div className="flex items-center">
                                    <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" 
                                            d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
                                    </svg>
                                    <span>Difficulty: {session.trail?.difficulty || 'N/A'}</span>
                                </div>
                                <div className="flex items-center">
                                    <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" 
                                            d="M9 20l-5.447-2.724A1 1 0 013 16.382V5.618a1 1 0 011.447-.894L9 7m0 13l6-3m-6 3V7m6 10l4.553 2.276A1 1 0 0021 18.382V7.618a1 1 0 00-.553-.894L15 4m0 13V4m0 0L9 7" />
                                    </svg>
                                    <span>Length: {session.trail?.length || 'N/A'}</span>
                                </div>
                            </div>

<div className="mt-4 space-y-4 text-center">
  {session.status === 'pending' ? (
    <div className="flex gap-4 justify-center">
      <button 
        onClick={() => onCancelMatch(session.id)}
        className="w-full md:w-auto min-w-[140px] px-5 py-2 bg-red-600 text-white rounded-full text-sm font-semibold hover:bg-red-700"
      >
        Reject
      </button>
      <button 
        onClick={() => onAcceptMatch(session.id)}
        className="w-full md:w-auto min-w-[140px] px-5 py-2 bg-green-600 text-white rounded-full text-sm font-semibold hover:bg-green-700"
      >
        Accept
      </button>
    </div>
  ) : session.status === 'completed' ? (
    <div className="flex flex-wrap gap-2 justify-center">
      <button 
        onClick={() => onViewProfile(session.otherProfile, true)}
        className="w-full md:w-auto min-w-[140px] px-5 py-2 bg-white border-2 border-black-900 text-black-600 rounded-full text-sm font-semibold"
      >
        View Profile
      </button>
      <button 
        onClick={() => handleViewTrailDetails(session.trail_id, session, 'agenda')}
        className="w-full md:w-auto min-w-[140px] px-5 py-2 bg-gradient-to-r from-[#6B01D1] to-[#F2787A] text-white rounded-full text-sm font-semibold"
      >
        View Trail Details
      </button>
      {session.canProvideFeedback && (
        <button 
          onClick={() => onFeedbackRequest(session)}
          className="w-full md:w-auto min-w-[140px] px-5 py-2 bg-gradient-to-r from-[#6B01D1] to-[#F2787A] text-white rounded-full text-sm font-semibold"
        >
          Rate Your Experience
        </button>
      )}
    </div>
  ) : session.status === 'confirmed' ? (
    <>
      <button 
        onClick={() => onViewProfile(session.otherProfile, true)}
        className="w-full md:w-auto min-w-[140px] px-5 py-2 bg-white border-2 border-black-900 text-black-600 rounded-full text-sm font-semibold hover:bg-black-50"
      >
        View Profile
      </button>
      <button 
        onClick={() => handleViewTrailDetails(session.trail_id, session, 'agenda')}
        className="w-full md:w-auto min-w-[140px] px-5 py-2 bg-gradient-to-r from-[#6B01D1] to-[#F2787A] text-white rounded-full text-sm font-semibold"
      >
        View Trail Details
      </button>
    </>
  ) : (
    <div className="flex gap-2 justify-center">
      <button 
        onClick={() => onCancelMatch(session.id)}
        className="w-full md:w-auto min-w-[140px] px-5 py-2 bg-neutral-800 text-white rounded-full text-sm font-semibold mb-2"
      >
        Cancel Search
      </button>
      <button 
        onClick={() => handleViewTrailDetails(session.trail_id, session, 'agenda')}
        className="w-full md:w-auto min-w-[140px] px-5 py-2 bg-gradient-to-r from-[#6B01D1] to-[#F2787A] text-white rounded-full text-sm font-semibold mb-2"
      >
        View Trail Details
      </button>
    </div>
  )}
</div>

                        </div>
                    );
                })}
                {sortedSessions.length === 0 && (
                    <div className="text-center py-12">
                        <h3 className="text-lg font-semibold text-gray-500">
                            No sessions on this date
                        </h3>
                    </div>
                )}
            </div>
        </div>
    );
};

export default AgendaView; 


